import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  CheckResult,
  CheckResultStatus,
  dismissDesignRuleCheck,
  MountType,
} from "../../../../reducers/uploadedBoard";
import SVGViewer from "../../../common/SVGViewer";
import LoadingIcon from "../../../common/LoadingIcon";
import {
  DetailsContainer,
  DetailsSidebarContainer,
  LearnMore,
  LocationListItem,
  LocationsList,
  Result,
  ResultTitle,
} from "./RuleChecksStyles";
import { xyformatting } from "../../../../utils/valueFormatting";
import { EXPECTED_PLACEMENT_UNIT } from "../../BillOfMaterials/SelectPart/UpdateElementPlacement";
import { InputLabel } from "../../../../constants/Styles";
import { ErrorIcon, ResultMessage, WarningIcon } from "./ResultLine";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/store";
import { Focus, setFocus } from "../../../../reducers/svgViewer";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../../utils/group";
import Button from "../../../common/Button";
import LoadingOverlay from "../../../LoadingOverlay";
import { useSendUpdateEvent } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/sendUpdates";
import {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import { useArrowKeys } from "../../../../utils/hooks/arrowKeys";

const LOCATION_DIMENSION_SIZE = 2;

function locationToFocus(x: number, y: number): Focus {
  return {
    dimensions: {
      minX: x - LOCATION_DIMENSION_SIZE / 2,
      minY: y - LOCATION_DIMENSION_SIZE / 2,
      width: LOCATION_DIMENSION_SIZE,
      height: LOCATION_DIMENSION_SIZE,
    },
    mountType: MountType.TOP,
  };
}

function Locations({ result }: { result: CheckResult }) {
  const dispatch = useAppDispatch();
  const [selectedLocationIndex, setSelectedLocationIndex] = useState<
    number | null
  >(null);

  const moveToLocation = useCallback(
    (focus: Focus) => {
      dispatch(setFocus({ focus }));
    },
    [dispatch],
  );

  useEffect(() => {
    if (result.locations !== null && result.locations.length > 0) {
      if (selectedLocationIndex === null) {
        setSelectedLocationIndex(0);
      } else {
        const x = result.locations[selectedLocationIndex]![0]!;
        const y = result.locations[selectedLocationIndex]![1]!;
        moveToLocation(locationToFocus(x, y));
      }
    }
  }, [selectedLocationIndex, result.locations, moveToLocation]);

  const moveToNextLocation = useCallback(() => {
    if (selectedLocationIndex !== null && result.locations !== null) {
      setSelectedLocationIndex(
        (selectedLocationIndex + 1) % result.locations.length,
      );
    }
  }, [result.locations, selectedLocationIndex]);

  const moveToPreviousLocation = useCallback(() => {
    if (selectedLocationIndex !== null && result.locations !== null) {
      const nextLocation =
        selectedLocationIndex == 0
          ? result.locations.length - 1
          : selectedLocationIndex - 1;
      setSelectedLocationIndex(nextLocation);
    }
  }, [result.locations, selectedLocationIndex]);

  useArrowKeys({
    onArrowUp: moveToPreviousLocation,
    onArrowDown: moveToNextLocation,
  });

  if (result.locations === null || result.locations.length == 0) {
    return null;
  }

  const formattedLocations = result.locations.map((location, locationIndex) => {
    const x = location[0]!;
    const y = location[1]!;
    const formattedLocation = xyformatting(x, y, EXPECTED_PLACEMENT_UNIT);
    return (
      <LocationListItem
        key={formattedLocation + locationIndex}
        onClick={() => setSelectedLocationIndex(locationIndex)}
        selected={selectedLocationIndex == locationIndex}
      >
        {formattedLocation}
      </LocationListItem>
    );
  });

  return (
    <div>
      <InputLabel>Locations</InputLabel>
      <LocationsList>{formattedLocations}</LocationsList>
    </div>
  );
}

export default function CheckDetails({ result }: { result: CheckResult }) {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const { groups } = useAppSelector((state) => state.auth);
  const userIsAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const images = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.images || null,
  );
  const { sendUpdateEvent, loading: dismissing } = useSendUpdateEvent();

  const handleDismissDesignRuleCheck = () => {
    void sendUpdateEvent({
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.SHOULD_DISMISS_DRC,
      update: { dismiss_check_update: { result_id: result.result_id } },
    });
    if (boardId) {
      dispatch(dismissDesignRuleCheck({ boardId, resultId: result.result_id }));
    }
  };

  if (images !== null) {
    const layerSVG = images.layers.find((layer) => layer.name == result.layer);
    if (layerSVG !== undefined && layerSVG.svg !== null) {
      const overlay = result.shape_svg_url
        ? {
            input: { svgUrl: result.shape_svg_url },
            placement: {
              x: 0,
              y: 0,
              mount_type: MountType.TOP,
              rotation_degrees: 0,
              unit: "mm",
            },
          }
        : undefined;
      return (
        <DetailsContainer>
          <LoadingOverlay loading={dismissing} text={"Dismissing check"} />
          <SVGViewer svgInput={{ svgUrl: layerSVG.svg }} overlay={overlay} />
          <DetailsSidebarContainer>
            <Result>
              {result.status == CheckResultStatus.DISMISSED && <WarningIcon />}
              {result.status == CheckResultStatus.WARNING && <WarningIcon />}
              {result.status == CheckResultStatus.ERROR && <ErrorIcon />}
              <ResultTitle>{result.status}</ResultTitle>
            </Result>
            {result.status == CheckResultStatus.DISMISSED && (
              <LearnMore>
                The design rule check completed unsuccessfully but a FlashPCB
                engineer has determined that it will not impede manufacturing.
              </LearnMore>
            )}
            <div>
              <InputLabel>Message</InputLabel>
              <ResultMessage result={result} />
            </div>
            <Locations result={result} />
            <LearnMore center>
              <a
                href={"/docs/uploading-your-board#running-design-rule-checks"}
                target={"_blank"}
              >
                What does this mean for me?
              </a>
            </LearnMore>
            {userIsAdminOrEngineer &&
              result.status == CheckResultStatus.ERROR && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    label={"Dismiss error"}
                    onClick={handleDismissDesignRuleCheck}
                  />
                </div>
              )}
          </DetailsSidebarContainer>
        </DetailsContainer>
      );
    }
  }
  return <LoadingIcon />;
}
